'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { DataTable } from '@/components/ui/data-table';
import type { MarketTraderViewModel } from './trader-row';

// Shared cell set for a market-traders row. Rendered both by the scrolling
// table and by the pinned "Your rank" card so the two layouts cannot drift.

const SECONDARY_METRIC_CELL_CLASS: string = 'hidden w-24 min-w-0 max-xl:flex max-xl:w-full max-xl:flex-col max-xl:gap-0.5 xl:table-cell';
const TOTAL_METRIC_CELL_CLASS: string = 'md:w-24 xl:w-32 max-xl:flex max-xl:min-w-0 max-xl:w-full max-xl:flex-col max-xl:gap-0.5';
interface TraderRowCellsProps {
  entry: MarketTraderViewModel;
  /** Marks the viewer's row (current-user highlight + inline "You" badge). */
  isUser: boolean;
}
export function TraderRowCells(t0) {
  const $ = _c(41);
  const {
    entry,
    isUser
  } = t0;
  const t = useTranslations("market.traders");
  const tTradeValidation = useTranslations("trade.validation");
  let t1;
  if ($[0] !== tTradeValidation) {
    t1 = tTradeValidation("quote_unavailable");
    $[0] = tTradeValidation;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== tTradeValidation) {
    t2 = tTradeValidation("quote_unavailable");
    $[2] = tTradeValidation;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span className="sr-only">{t2}</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span aria-hidden="true">—</span>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t3) {
    t5 = <span title={t1}>{t3}{t4}</span>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const unavailable = t5;
  let t6;
  if ($[10] !== entry.rank || $[11] !== isUser) {
    t6 = <DataTable.RankCell value={entry.rank} isCurrentUser={isUser} />;
    $[10] = entry.rank;
    $[11] = isUser;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== entry.trader || $[14] !== isUser) {
    t7 = <DataTable.IdentityCell user={entry.trader} showYouBadge={isUser} />;
    $[13] = entry.trader;
    $[14] = isUser;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = entry.totalVolumeD6;
  let t9;
  if ($[16] !== t) {
    t9 = t("volume");
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== entry.totalVolumeD6 || $[19] !== t9) {
    t10 = <DataTable.NumericCell value={t8} format="usdAdaptive" mobileLabel={t9} className={SECONDARY_METRIC_CELL_CLASS} />;
    $[18] = entry.totalVolumeD6;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== entry.unrealizedPnlD6 || $[22] !== t || $[23] !== unavailable) {
    t11 = entry.unrealizedPnlD6 === null ? <DataTable.TextCell className={SECONDARY_METRIC_CELL_CLASS} tone="tertiary" mobileLabel={t("unrealized_pnl")}>{unavailable}</DataTable.TextCell> : <DataTable.NumericCell value={entry.unrealizedPnlD6} format="usdAdaptiveSigned" tone="auto" showThresholdIcon={true} mobileLabel={t("unrealized_pnl")} className={SECONDARY_METRIC_CELL_CLASS} />;
    $[21] = entry.unrealizedPnlD6;
    $[22] = t;
    $[23] = unavailable;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = entry.realizedPnlD6;
  let t13;
  if ($[25] !== t) {
    t13 = t("realized_pnl");
    $[25] = t;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== entry.realizedPnlD6 || $[28] !== t13) {
    t14 = <DataTable.NumericCell value={t12} format="usdAdaptiveSigned" tone="auto" showThresholdIcon={true} mobileLabel={t13} className={SECONDARY_METRIC_CELL_CLASS} />;
    $[27] = entry.realizedPnlD6;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== entry.totalPnlD6 || $[31] !== t || $[32] !== unavailable) {
    t15 = entry.totalPnlD6 === null ? <DataTable.TextCell className={TOTAL_METRIC_CELL_CLASS} tone="tertiary" mobileLabel={t("total_pnl")}>{unavailable}</DataTable.TextCell> : <DataTable.NumericCell value={entry.totalPnlD6} format="usdAdaptiveSigned" tone="auto" showThresholdIcon={true} animated={true} animationDuration={300} mobileLabel={t("total_pnl")} className={TOTAL_METRIC_CELL_CLASS} />;
    $[30] = entry.totalPnlD6;
    $[31] = t;
    $[32] = unavailable;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t14 || $[37] !== t15 || $[38] !== t6 || $[39] !== t7) {
    t16 = <>{t6}{t7}{t10}{t11}{t14}{t15}</>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t14;
    $[37] = t15;
    $[38] = t6;
    $[39] = t7;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  return t16;
}